'use client';

import type Matter from 'matter-js';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

const images = [
  '/images/404/1.svg',
  '/images/404/2.svg',
  '/images/404/3.svg',
  '/images/404/4.svg',
  '/images/404/5.svg',
  '/images/404/6.svg',
  '/images/404/7.svg',
  '/images/404/8.svg',
  '/images/404/9.svg',
  '/images/404/10.svg',
  '/images/404/11.svg',
  '/images/404/12.svg',
  '/images/404/13.svg',
  '/images/404/14.svg',
  '/images/404/15.svg',
  '/images/404/16.svg',
  '/images/404/17.svg',
  '/images/404/18.svg',
  '/images/404/19.svg',
  '/images/404/20.svg',
  '/images/404/21.svg',
  '/images/404/22.svg',
  '/images/404/23.svg',
  '/images/404/24.svg',
];

const preloadImages = (): Promise<
  Map<string, { width: number; height: number }>
> => {
  return new Promise((resolve) => {
    const imageSizes = new Map<string, { width: number; height: number }>();
    let loaded = 0;

    images.forEach((src) => {
      const img = new Image();
      img.onload = () => {
        imageSizes.set(src, { width: img.width, height: img.height });
        loaded++;
        if (loaded === images.length) {
          resolve(imageSizes);
        }
      };

      img.onerror = () => {
        imageSizes.set(src, { width: 100, height: 100 });
        loaded++;

        if (loaded === images.length) {
          resolve(imageSizes);
        }
      };

      img.src = src;
    });
  });
};

const getMaxRadius = (width: number, height: number) => {
  if (width < 768) {
    return 40;
  }

  if (height < 800) {
    return 50;
  }

  return 75;
};

export default function NotFound() {
  const sceneRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<Matter.Engine | null>(null);
  const renderRef = useRef<Matter.Render | null>(null);
  const runnerRef = useRef<Matter.Runner | null>(null);
  const [matterModule, setMatterModule] = useState<typeof Matter | null>(null);

  const [imageSizes, setImageSizes] = useState<
    Map<string, { width: number; height: number }>
  >(new Map());
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [locale, setLocale] = useState<'ru' | 'en'>();

  useEffect(() => {
    const locale = document.cookie
      .split('; ')
      .find((row) => row.startsWith('NEXT_LOCALE='))
      ?.split('=')[1];

    if (locale === 'ru' || !locale) {
      setLocale('ru');
    } else {
      setLocale('en');
    }
  }, []);

  useEffect(() => {
    preloadImages().then((sizes) => {
      setImageSizes(sizes);
      setImagesLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('matter-js').then((module) => {
        setMatterModule(module);
      });
    }
  }, []);

  useEffect(() => {
    if (
      !matterModule ||
      !imagesLoaded ||
      !sceneRef.current ||
      !canvasRef.current
    )
      return;

    const isMobile = window.innerWidth < 768;

    const {
      Engine,
      Render,
      Runner,
      Bodies,
      Composite,
      Mouse,
      MouseConstraint,
      World,
    } = matterModule;

    const engine = Engine.create({
      gravity: {
        x: 0,
        y: 1,
        scale: 0.001,
      },
    });
    engineRef.current = engine;

    const sceneWidth = sceneRef.current.clientWidth;
    const sceneHeight = sceneRef.current.clientHeight;

    const render = Render.create({
      canvas: canvasRef.current,
      engine: engine,
      options: {
        width: sceneWidth,
        height: sceneHeight,
        wireframes: false,
        background: 'transparent',
      },
    });
    renderRef.current = render;

    Render.run(render);

    const runner = Runner.create();
    runnerRef.current = runner;
    Runner.run(runner, engine);

    const topWallOffset = -2000;
    const wallThickness = 1000;
    const wallExtension = 1000;

    const wallOptions = { isStatic: true, render: { visible: false } };
    Composite.add(engine.world, [
      Bodies.rectangle(
        sceneWidth / 2,
        topWallOffset - wallThickness / 2,
        sceneWidth + wallExtension * 2,
        wallThickness,
        wallOptions,
      ),
      Bodies.rectangle(
        sceneWidth / 2,
        sceneHeight + wallThickness / 2,
        sceneWidth + wallExtension * 2,
        wallThickness,
        wallOptions,
      ),
      Bodies.rectangle(
        -wallThickness / 2,
        sceneHeight / 2,
        wallThickness,
        sceneHeight * 4,
        wallOptions,
      ),
      Bodies.rectangle(
        sceneWidth + wallThickness / 2,
        sceneHeight / 2,
        wallThickness,
        sceneHeight * 4,
        wallOptions,
      ),
    ]);

    const bodies: Matter.Body[] = [];
    const totalCircles = 30;
    const minRadius = isMobile ? 20 : 25;
    const maxRadius = getMaxRadius(sceneWidth, sceneHeight);

    const startingHeight = -500;
    const rows = 5;
    const cols = Math.ceil(totalCircles / rows);

    const cellWidth = maxRadius * 3;
    const cellHeight = maxRadius * 3;

    const gridWidth = cols * cellWidth;
    const startX = (sceneWidth - gridWidth) / 2 + cellWidth / 2;

    const positions = [];

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (positions.length < totalCircles) {
          const randomOffsetX = (Math.random() - 0.5) * cellWidth * 0.5;
          const randomOffsetY = (Math.random() - 0.5) * cellHeight * 0.5;

          positions.push({
            x: startX + col * cellWidth + randomOffsetX,
            y: startingHeight - row * cellHeight + randomOffsetY,
          });
        }
      }
    }

    for (let i = positions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [positions[i], positions[j]] = [positions[j], positions[i]];
    }

    for (let i = 0; i < totalCircles; i++) {
      const imageIndex = i % images.length;
      const img = images[imageIndex];

      const circleRadius =
        Math.floor(Math.random() * (maxRadius - minRadius + 1)) + minRadius;

      const position = positions[i];

      const circle = Bodies.circle(position.x, position.y, circleRadius, {
        restitution: 0.5,
        friction: 0.01,
        frictionAir: 0.005,
        density: 0.0005,
        render: {
          sprite: {
            texture: img,
            xScale: (circleRadius * 2) / (imageSizes.get(img)?.width || 100),
            yScale: (circleRadius * 2) / (imageSizes.get(img)?.height || 100),
          },
        },
      });

      bodies.push(circle);
    }

    Composite.add(engine.world, bodies);

    const mouse = Mouse.create(render.canvas);
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse: mouse,
      constraint: {
        stiffness: 0.2,
        render: {
          visible: false,
        },
      },
    });

    World.add(engine.world, mouseConstraint as any);

    render.mouse = mouse;

    const handleResize = () => {
      if (!sceneRef.current || !render.canvas) return;

      const newWidth = sceneRef.current.clientWidth;
      const newHeight = sceneRef.current.clientHeight;

      render.options.width = newWidth;
      render.options.height = newHeight;
      render.canvas.width = newWidth;
      render.canvas.height = newHeight;

      Composite.clear(engine.world, false);
      Composite.add(engine.world, [
        Bodies.rectangle(
          newWidth / 2,
          topWallOffset - wallThickness / 2,
          newWidth + wallExtension * 2,
          wallThickness,
          wallOptions,
        ),
        Bodies.rectangle(
          newWidth / 2,
          newHeight + wallThickness / 2,
          newWidth + wallExtension * 2,
          wallThickness,
          wallOptions,
        ),
        Bodies.rectangle(
          -wallThickness / 2,
          newHeight / 2,
          wallThickness,
          newHeight * 4,
          wallOptions,
        ),
        Bodies.rectangle(
          newWidth + wallThickness / 2,
          newHeight / 2,
          wallThickness,
          newHeight * 4,
          wallOptions,
        ),
      ]);

      Composite.add(engine.world, bodies);

      World.add(engine.world, mouseConstraint as any);
    };

    window.addEventListener('resize', handleResize);

    render.canvas.addEventListener('mousedown', () => {
      render.canvas.style.pointerEvents = 'auto';
    });

    render.canvas.addEventListener(
      'wheel',
      () => {
        render.canvas.style.pointerEvents = 'none';

        setTimeout(() => {
          render.canvas.style.pointerEvents = 'auto';
        }, 100);
      },
      { passive: true },
    );

    return () => {
      window.removeEventListener('resize', handleResize);

      if (renderRef.current) Render.stop(renderRef.current);
      if (runnerRef.current) Runner.stop(runnerRef.current);

      if (engineRef.current) {
        Composite.clear(engineRef.current.world, false);
        Engine.clear(engineRef.current);
      }
    };
  }, [matterModule, imagesLoaded, imageSizes]);

  return (
    <>
      <div className="text-text-base-primary relative flex min-h-[max(calc(100vh-220px),600px)] flex-col items-center overflow-hidden bg-[#1c2126] max-md:h-[100vh]">
        <h1
          className="m-0 text-[338px] leading-none font-bold opacity-70 max-md:mt-[120px] max-md:text-[140px]"
          style={{
            background:
              'linear-gradient(104.45deg, #FFFFFF 15.38%, #192025 96.94%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          404
        </h1>

        {!!locale && (
          <div className="max-md:max-5 absolute top-[225px] z-10 max-w-[564px] rounded-[24px] bg-[#1c2126]/70 px-9 py-8 text-center backdrop-blur-[20px] max-md:top-[215px] max-md:mx-4 max-md:p-4 max-md:px-5">
            <div
              className="pointer-events-none absolute inset-0 rounded-[24px] p-[1px]"
              style={{
                background:
                  'linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(25, 32, 37, 0.08) 117.59%)',
                WebkitMask:
                  'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            <h2 className="text-[28px] leading-[38px] font-medium max-md:text-[24px]">
              {translations[locale].title}
            </h2>

            <p className="text-text-base-secondary mt-4 text-[16px] leading-[24px] font-normal max-md:mt-2 max-md:text-[15px]">
              {translations[locale].description}
            </p>
            <Link
              aria-label="btn"
              href="/"
              className="text-basePrimary bg-surface-base-inverse-primary hover:bg-custom-base-inverse-secondary mx-auto mt-6 flex w-fit items-center justify-center rounded rounded-[12px] px-6 py-3 text-[16px] leading-[24px] font-medium transition-colors duration-300 max-md:h-[56px] max-md:w-full"
            >
              {translations[locale].button}
            </Link>
          </div>
        )}

        <div
          ref={sceneRef}
          className="pointer-events-none absolute top-0 left-0 z-0 h-full w-full"
        >
          <canvas
            ref={canvasRef}
            className="pointer-events-auto h-full w-full"
          />
        </div>
      </div>
    </>
  );
}

const translations = {
  ru: {
    title: 'Страница не найдена',
    description:
      'Ссылка, по которой вы хотели перейти, не существует. Возможно, в ней была допущена опечатка или запрашиваемая страница удалена.',
    button: 'Перейти на главную страницу',
  },
  en: {
    title: 'Page not found',
    description:
      'The link you tried to access does not exist. It may have been a typo or the page has been removed.',
    button: 'Go to the main page',
  },
};
